import React from 'react';
import Button from '../ui/button';
import SectionBackground from '../sections/section-background';
import Breadcrumb from '../ui/breadcrumb';

interface HeroTemplateProps {
  title: string | React.ReactNode;
  eyebrow?: React.ReactNode;
  description: string | React.ReactNode;
  /**
   * URL d'illustration externe. Affichée en arrière-plan absolu côté droit.
   * Optionnelle quand `customRight` est fourni.
   */
  imageUrl?: string;
  /**
   * Slot pour une illustration codée (SVG inline, composant React).
   * Quand fourni, remplace `imageUrl` et s'intègre dans la 2e colonne
   * du grid pour un rendu plus net et plus design.
   */
  customRight?: React.ReactNode;
  cta1?: {
    label: string;
    href?: string;
    onClick?: () => void;
    external?: boolean;
  };
  cta2?: {
    label: string;
    href?: string;
    onClick?: () => void;
    external?: boolean;
  };
  page: string;
  section?: string;
  breadcrumbs?: {
    name: string;
    href: string;
  }[];
}

export default function HeroTemplateEnts({
  title,
  eyebrow,
  description,
  imageUrl,
  customRight,
  cta1,
  cta2,
  page,
  section = 'hero',
  breadcrumbs,
}: HeroTemplateProps) {
  const useCustomRight = !!customRight;

  return (
    <SectionBackground
      page={page}
      section={section}
      className="relative overflow-hidden bg-gradient-to-br from-[#e0f2ff] via-[#f3e8ff] to-[#fdf4ff]"
    >
      {/* Image absolue (legacy) — affichée seulement si customRight n'est pas fourni */}
      {!useCustomRight && imageUrl && (
        <div className="absolute inset-0 z-0 flex justify-end items-center pointer-events-none select-none">
          <img
            src={imageUrl}
            alt="Illustration recrutement IA"
            className="h-[75%] object-contain max-h-[600px] md:pr-[5vw] opacity-20 md:opacity-100"
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
        </div>
      )}

      {/*
        Hero contraint à 1 écran sur 13 pouces — règle UX "une section
        = un écran". On vise min-h 88vh + tailles compactes pour que le
        contenu tienne dans la zone visible (1280×800 typique) sans
        forcer l'utilisateur à scroller pour voir les CTA.
      */}
      <section className="relative z-10 min-h-[88vh] flex items-center py-10 md:py-12 lg:py-14">
        <div className="w-full px-[5vw] xl:px-[100px] max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-2 items-center gap-10">
          <div className="max-w-2xl">

            {/* Accroche au-dessus du H1 — petite taille, uppercase, discrète */}
            {eyebrow && (
              <p className="text-xs md:text-sm font-semibold text-neutral-500 mb-3 leading-snug uppercase tracking-widest">
                {eyebrow}
              </p>
            )}

            {/* H1 — tailles réduites pour tenir dans 1 écran 13"
                tout en restant impactant sur grands écrans.
                leading 1.1 + pb-1 pour ne pas clipper les descenders
                (g, p, j, y) avec bg-clip-text. */}
            <h1 className={
              typeof title === 'string'
                ? 'text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-title font-extrabold leading-[1.1] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254] mb-0'
                : 'text-4xl md:text-5xl lg:text-6xl font-title font-extrabold leading-[1.1] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]'
            }>
              {title}
            </h1>

            {breadcrumbs && (
              <div className="my-3">
                <Breadcrumb items={breadcrumbs} />
              </div>
            )}

            <div className="mt-5 mb-6 text-neutral-800 text-base md:text-lg leading-relaxed">
              {description}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              {cta1 && (
                <Button
                  variant="gradient"
                  size="lg"
                  href={cta1.href}
                  onClick={cta1.onClick}
                  target={cta1.external ? '_blank' : undefined}
                  rel={cta1.external ? 'noopener noreferrer' : undefined}
                >
                  {cta1.label}
                </Button>
              )}
              {cta2 && (
                <Button
                  variant="secondary"
                  size="lg"
                  href={cta2.href}
                  onClick={cta2.onClick}
                  target={cta2.external ? '_blank' : undefined}
                  rel={cta2.external ? 'noopener noreferrer' : undefined}
                >
                  {cta2.label}
                </Button>
              )}
            </div>
          </div>

          {/* Slot illustration custom — 2ème colonne du grid quand fourni */}
          {useCustomRight && (
            <div className="hidden md:block">
              {customRight}
            </div>
          )}
        </div>
      </section>
    </SectionBackground>
  );
}
